import { PluginMessageTypes } from './constants';
import {
  setCalledByParent, isCalledByParent, getCallerParentId, setCallerExtraParams,
} from './caller';
import Log from '../utils/log';
import { getUUID } from './config';
import { getWindowReceiverData, setWindowReceiverData } from './windowData';
import packageInfo from '../package.json';

/**
 * Libreria che consente di far comunicare il parent e le applicazioni
 * caricate negli iframe
 */

const pluginsVersion = packageInfo.version;

// identificativo dei messaggi riconosciuti dal framework (export solo per test)
export const ID_PLUGIN_MSGS = 'FW-PLUGINS-MSGS';

// numero massimo di ultimi uid dei messaggi memorizzati
const NUM_MAX_LAST_MSG_ID = 10;

// oggetti mantenuti nella sezione windows del receiver
const CALLBACK_REGS = 'regs';
const LAST_MESSAGES = 'lastMessages';

/**
 * Classe che gestisce le comunicazioni
 */
export class PluginMessages {
  constructor() {
    this.config = {
      initialized: false,
      IdReceiver: null, // identificativo del ricevente
    };

    this.isOnLastMessages = (uuid) => {
      // per i vecchi messaggi dove uuid non esiste
      // non gestisco questa funzionalità
      if (!uuid) return false;
      const lastMessages = getWindowReceiverData(this.config.idReceiver, LAST_MESSAGES);
      const isOnLast = lastMessages.includes(uuid);
      if (!isOnLast) {
        lastMessages.push(uuid);
        if (lastMessages.length > NUM_MAX_LAST_MSG_ID) {
          lastMessages.shift();
        }
      }
      return isOnLast;
    };

    this.handleMessage = (event) => {
      if (event.data && event.data.id === ID_PLUGIN_MSGS
        // scarto i messaggi che sono stati generati o inoltrati dal receiver corrente
        && event.data.origin !== this.config.idReceiver
        && event.data.initial !== this.config.idReceiver
        && !this.isOnLastMessages(event.data.uuid)) {
        if (event.data.destination && event.data.destination !== this.config.idReceiver) {
          // evento non destinato al receiver corrente
          // se presente un parent allora inoltro l'evento
          if (isCalledByParent()) {
            // se non è arrivato dallo stesso parent
            if (event.data.origin !== getCallerParentId()) {
              // lo inoltro al padre
              this.forwardMessageToParent(event.data);
            }
          }
          // inoltro l'evento ai figli
          this.forwardMessageToChilds(event.data);
          return;
        }
        Log.debug(`Messaggio inviato da "${event.data.origin}" a "${this.config.idReceiver}" [${event.data.type}]`, event);
        const regs = getWindowReceiverData(this.config.idReceiver, CALLBACK_REGS);
        regs.forEach((reg) => {
          reg.callback(event.data, reg.context);
        });
        // gestione extra-params
        if (event.data.type === PluginMessageTypes.EXTRA_PARAMS) {
          setCallerExtraParams(event.data.payload);
        }
      }
    };

    this.configTest = () => {
      if (!this.config.initialized) {
        throw new TypeError('PluginMessages non ancora configurato');
      }
    };

    this.formatMessage = (type, payload, destination = null) => {
      this.configTest();
      return {
        uuid: getUUID(), // message uuid
        id: ID_PLUGIN_MSGS, // identificativo dei messaggi del framework
        initial: this.config.idReceiver, // il primo creatore del messaggio
        initialVersion: pluginsVersion, // versione del primo creatore del messaggio
        origin: this.config.idReceiver, // mittente del messaggio
        originVersion: pluginsVersion, // versione del mittente del messaggio
        destination,
        type,
        payload,
      };
    };

    this.formatForwardMessage = (message) => {
      this.configTest();
      return {
        uuid: message.uuid || getUUID(), // se non esiste (vecchio formato lo inserisco)
        id: ID_PLUGIN_MSGS, // identificativo dei messaggi del framework
        initial: message.initial || message.origin, // il primo creatore del messaggio (origin nel caso di vecchi formati)
        origin: this.config.idReceiver, // mittente del messaggio
        originVersion: pluginsVersion, // versione del mittente del messaggio
        destination: message.destination,
        type: message.type,
        payload: message.payload,
      };
    };

    this.getIdReceiver = () => {
      this.configTest();
      return this.config.idReceiver;
    };
  }

  /**
   * Metodo utilizzato dal parent e dalle app per inizializzare la libreria
   * @param {string} idReceiver - identificativo univoco dell'applicativo
   */
  init = (idReceiver) => {
    if (this.config.initialized) {
      Log.warn(`PluginMessages già configurato per ${idReceiver}`);
      // return;
    }
    // settings configurations
    this.config.idReceiver = idReceiver;
    setWindowReceiverData(idReceiver, CALLBACK_REGS, []);
    setWindowReceiverData(idReceiver, LAST_MESSAGES, []);
    this.config.initialized = true;
    // settaggio attributo callerParent
    setCalledByParent();
    // invio messaggio per segnalare l'avvenuto caricamento
    if (isCalledByParent()) this.postMessageToParent(PluginMessageTypes.LOADED);
    // configurazione eventi
    if (window.addEventListener) {
      window.addEventListener('message', this.handleMessage);
    } else {
      window.attachEvent('onmessage', this.handleMessage);
    }
  };

  /**
   * Metodo utilizzato dal parent per inviare messaggi
   * all'applicativo caricato nell'iframe (con idDestination
   * verrà inoltrato l'evento ad un solo specifico applicativo)
   * @param {string} type - tipologia del messaggio
   * @param {object} payload - messaggio
   * @param {string} idDestination - [opzionale] eventuale identificativo del destinatario
   */
  postMessageToChilds = (type, payload, idDestination = null) => {
    this.configTest();
    const iframes = document.getElementsByTagName('iframe');
    for (let i = 0; i < iframes.length; i += 1) {
      iframes[i].contentWindow.postMessage(this.formatMessage(type, payload, idDestination), '*');
    }
  };

  /**
   * Metodo utilizzato per forwardare un messaggio ricevuto verso i childs
   * @param {*} message
   */
  forwardMessageToChilds = (message) => {
    this.configTest();
    const iframes = document.getElementsByTagName('iframe');
    for (let i = 0; i < iframes.length; i += 1) {
      iframes[i].contentWindow.postMessage(this.formatForwardMessage(message), '*');
    }
  };

  /**
   * Metodo utilizzato dal parent per inoltrare eventi signalR
   * all'applicativo caricato nell'iframe (con idDestination
   * verrà inoltrato l'evento ad un solo specifico applicativo)
   * @param {string} eventName - nome dell'evento signalR
   * @param {object} event - evento signalR da inoltrare
   * @param {string} idDestination - [opzionale] eventuale identificativo del destinatario
   */
  forwardEventoServerToChilds = (eventName, event, idDestination = null) => {
    this.postMessageToChilds(
      PluginMessageTypes.SERVER_EVENT,
      { eventName, event },
      idDestination,
    );
  };

  /**
   * Metodo utilizzato dagli applicativi nell'iframe per
   * inviare messaggi al parent
   * @param {string} type - tipologia del messaggio
   * @param {object} payload - messaggio
   * @param {string} idDestination - [opzionale] eventuale identificativo del destinatario
   */
  postMessageToParent = (type, payload, idDestination = null) => {
    this.configTest();
    window.parent.postMessage(this.formatMessage(type, payload, idDestination), '*');
  };

  /**
   * Metodo utilizzato per effettuare un forward al parent di un messaggio ricevuto
   * @param {*} message
   */
  forwardMessageToParent = (message) => {
    this.configTest();
    window.parent.postMessage(this.formatForwardMessage(message), '*');
  };

  /**
   * Metodo utilizzato dall'applicativo per inoltrare eventi signalR
   * al parent
   * @param {string} eventName - nome dell'evento signalR
   * @param {object} event - evento signalR da inoltrare
   */
  forwardEventoServerToParent = (eventName, event) => {
    this.postMessageToParent(
      PluginMessageTypes.SERVER_EVENT,
      { eventName, event },
    );
  };

  /**
   * Metodo per la sottoscrizione alla ricezione dei messaggi
   */
  subscribe = (callback, context = null) => {
    this.configTest();
    if (!callback || typeof callback !== 'function') {
      throw new TypeError('PluginMessages: Obbligatoria funzione callback per subscribe');
    }
    const regs = getWindowReceiverData(this.config.idReceiver, CALLBACK_REGS);
    regs.push({ callback, context });
    return this;
  };

  /**
   * Metodo utilizzato per rimuovere la sottoscrizione qui sopra descritta
   */
  unsubscribe = (callback) => {
    this.configTest();
    if (!callback || typeof callback !== 'function') {
      throw new TypeError('PluginMessages: Obbligatoria funzione callback per unsubscribe');
    }
    const regs = getWindowReceiverData(this.config.idReceiver, CALLBACK_REGS);
    for (let i = 0; i < regs.length; i += 1) {
      if (regs[i].callback === callback) {
        regs.splice(i, 1);
        return this;
      }
    }
    return this;
  };
}

const EventoServer = new PluginMessages();

export default EventoServer;
